
import { useCallback, useReducer } from 'react'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment/moment'

import { Model } from '../models/model'
import { invoiceReducer } from '../reducers/invoiceReducer'
import { calcDetraccion } from '../helpers/invoices'

const initialState = {
    detailInvoice: {
        id: 0,
        numero: '',
        referencia: null,
        id_moneda: null,
        simbolo: "S/",
        separacion_miles: ",",
        separacion_decimales: ".",
        id_cliente: null,
        cliente: null,
        id_tipo_documento: null,
        id_forma_pago: null,
        id_ppto: null,
        estado: 'por emitir',
        observaciones: null,
        fecha_emision: new Date(),
        fecha_vencimiento: moment(new Date()).add(30, 'days').format('YYYY-MM-DD'),
        referencia_origen: null,
        impuesto_porcentaje: 0,
        impuesto_nombre: '',
        impuesto_tipo_calculo: '',
        subtotal: 0,
        descuento: 0,
        monto_base: 0,
        total_impuesto: 0,
        total_factura: 0,
        total_cobrado: 0,
        total_por_cobrar: 0,
        saldo_por_facturar: 0,
        items: [],
        pays: [],
        reading_mode: false,
        valor_tipo_cambio: 0,
        aplica_detraccion: false,
        porcentaje_detraccion: 0,
        flow_id: null,
        total_detraccion: 0
    },
    invoices: []
}

export const useInvoices = () => {

    const [state, dispatch] = useReducer(invoiceReducer, initialState)
    const { invoices, detailInvoice } = state

    const initCreateInvoice = async (detailBudget, paramsDefault = null) => {

        let detalleFactura = detailBudget.referencia
        const saldoPorFacturar = detailBudget.total_por_facturar
        const referenciaOrigen = detailBudget.numero + " / " + detailBudget.referencia

        const newId = uuidv4()

        const invoiceItem = [{
            id: newId,
            nombre: detalleFactura,
            cantidad: 1,
            precio_unitario: saldoPorFacturar,
            subtotal: saldoPorFacturar,
            create: false
        }]

        const factorPorcentaje = (parseFloat(paramsDefault?.imp_venta_porcentaje) || 18) / 100
        const totalImpuesto = saldoPorFacturar * factorPorcentaje
        const totalFactura = saldoPorFacturar + totalImpuesto

        const infoDetraccion = await calcDetraccion({
            porcentaje_detraccion: 12,
            totalFactura,
            valor_tipo_cambio: detailBudget.valor_tipo_cambio,
            id_moneda: detailBudget.id_moneda,
            id_tipo_documento: paramsDefault?.id_tipo_documento_venta || 1
        })

        const newInvoice = {
            ...detailInvoice,
            id: 0,
            referencia: detalleFactura,
            id_moneda: detailBudget.id_moneda,
            simbolo: detailBudget.simbolo,
            separacion_miles: detailBudget.separacion_miles,
            separacion_decimales: detailBudget.separacion_decimales,
            id_cliente: detailBudget.id_cliente,
            cliente: detailBudget.cliente,
            id_ppto: detailBudget.id,
            estado: 'por emitir',
            referencia_origen: referenciaOrigen,
            subtotal: saldoPorFacturar,
            descuento: 0,
            monto_base: saldoPorFacturar,
            id_tipo_documento: paramsDefault?.id_tipo_documento_venta || 1, // factura
            id_forma_pago: paramsDefault?.id_forma_pago_venta || 2, // a 30 días
            impuesto_porcentaje: paramsDefault?.imp_venta_porcentaje || 18,
            total_impuesto: totalImpuesto,
            total_factura: totalFactura,
            total_cobrado: 0,
            total_por_cobrar: totalFactura,
            saldo_por_facturar: saldoPorFacturar,
            valor_tipo_cambio: detailBudget.valor_tipo_cambio,
            items: invoiceItem,
            cobros: [],
            reading_mode: false,
            aplica_detraccion: infoDetraccion.aplica_detraccion,
            porcentaje_detraccion: infoDetraccion.porcentaje_detraccion,
            total_detraccion: infoDetraccion.total_detraccion
        }

        const action = {
            type: '[INVOICE] set detail',
            payload: newInvoice
        }

        dispatch(action)

    }

    const initCreateInvoiceFromFlow = async (detailBudget, paramsDefault = null, infoFlow = null) => {

        let detalleFactura = infoFlow.referencia
        const saldoPorFacturar = parseFloat(infoFlow.monto_neto)
        const referenciaOrigen = detailBudget.numero + " / " + detailBudget.referencia
        const newId = uuidv4()

        const invoiceItem = [{
            id: newId,
            nombre: detalleFactura,
            cantidad: 1,
            precio_unitario: saldoPorFacturar,
            subtotal: saldoPorFacturar,
            create: false
        }]

        const idTipoDoc = infoFlow?.id_doc || (paramsDefault?.id_tipo_documento_venta || 1)
        const porcentajeImp = parseFloat(infoFlow?.porcentaje_impuesto || paramsDefault?.imp_venta_porcentaje)
        const factorPorcentaje = porcentajeImp / 100
        const totalImpuesto = saldoPorFacturar * factorPorcentaje
        const totalFactura = saldoPorFacturar + totalImpuesto
        // console.log({ porcentajeImp, totalFactura })

        const infoDetraccion = await calcDetraccion({
            porcentaje_detraccion: 12,
            totalFactura,
            valor_tipo_cambio: detailBudget.valor_tipo_cambio,
            id_moneda: detailBudget.id_moneda,
            id_tipo_documento: idTipoDoc
        })

        const newInvoice = {
            ...detailInvoice,
            id: 0,
            referencia: detalleFactura,
            id_moneda: detailBudget.id_moneda,
            simbolo: detailBudget.simbolo,
            separacion_miles: detailBudget.separacion_miles,
            separacion_decimales: detailBudget.separacion_decimales,
            id_cliente: detailBudget.id_cliente,
            cliente: detailBudget.cliente,
            id_ppto: detailBudget.id,
            estado: 'por emitir',
            referencia_origen: referenciaOrigen,
            subtotal: saldoPorFacturar,
            descuento: 0,
            monto_base: saldoPorFacturar,
            id_tipo_documento: idTipoDoc,
            id_forma_pago: paramsDefault?.id_forma_pago_venta || 2, // a 30 días
            impuesto_porcentaje: porcentajeImp,
            total_impuesto: totalImpuesto,
            total_factura: totalFactura,
            total_cobrado: 0,
            total_por_cobrar: totalFactura,
            saldo_por_facturar: saldoPorFacturar,
            valor_tipo_cambio: detailBudget.valor_tipo_cambio,
            items: invoiceItem,
            cobros: [],
            reading_mode: false,
            flow_id: infoFlow?.id,
            fecha_emision: infoFlow?.fecha_proyeccion,
            aplica_detraccion: infoDetraccion.aplica_detraccion,
            porcentaje_detraccion: infoDetraccion.porcentaje_detraccion,
            total_detraccion: infoDetraccion.total_detraccion
        }

        const action = {
            type: '[INVOICE] set detail',
            payload: newInvoice
        }

        dispatch(action)

    }

    const initGetAllInvoices = useCallback(async (filters = {}) => {

        const filtersText = (new URLSearchParams(filters)).toString()
        const params = {
            url: `invoices/?${filtersText}`
        }

        const dataList = await Model.getAll(params)

        const action = {
            type: '[INVOICE] Load list',
            payload: dataList
        }

        dispatch(action)

    }, [])

    const initGetOneInvoice = useCallback(async (invoiceId) => {

        const params = {
            url: `invoices/${invoiceId}`
        }

        const dataOne = await Model.getOne(params)

        const action = {
            type: '[INVOICE] set detail',
            payload: dataOne
        }

        dispatch(action)

    }, [])

    const initSaveInvoice = async (newInvoice, loadIniBudget) => {

        const params = {
            url: 'invoices',
            dataForm: newInvoice
        }

        const resultId = await Model.save(params)

        const newDetail = {
            ...newInvoice,
            id: resultId
        }

        if (resultId) {
            loadIniBudget()

            const action = {
                type: '[INVOICE] set detail',
                payload: newDetail
            }

            dispatch(action)

        }

    }

    const initUpdateInvoice = async (dataInvoice) => {
        const params = {
            url: `invoices/${dataInvoice.id}`,
            dataForm: dataInvoice
        }

        await Model.update(params)

    }

    // const initAnularInvoice = async (dataForm) => {
    //     const params = {
    //         url: `invoices/nullify/${dataForm.id}`,
    //         dataForm: dataForm
    //     }

    //     const resultUpdate = await Model.update(params)
    //     console.log({ resultUpdate })

    // }

    const initDeleteInvoice = async (dataForm) => {
        // const dataUpdate = await Model.updateAnular(dataForm)

        // if (dataUpdate.id) {
        //     const action = {
        //         type: '[BUY] set detail',
        //         payload: dataUpdate
        //     }
        //     dispatch(action)
        // }
    }

    return {
        invoices,
        detailInvoice,

        initCreateInvoice,
        initCreateInvoiceFromFlow,
        initGetAllInvoices,
        initGetOneInvoice,
        initSaveInvoice,
        initUpdateInvoice,
        // initAnularInvoice,
        initDeleteInvoice
    }

}